import classNames from 'classnames';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { HLSPlayer, State as PlayerStateEnum, parseSeconds, appendQueryParams, convertToPluralIfNeeded, downloadAlbumWithMediaToQueueItem, useHubSwitch, useViewport, Analytics, useUserSettings, CreateCardCarouselItem } from '@bpm-web-app/utils';
import { useDownloadAlbumById } from '@bpm-web-app/swr-hooks';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { useDrag } from '@use-gesture/react';
import styles from './mini-player.module.css';
import { usePlayer, usePlayerDuration } from '../player/player.context';
import { PlayerWave } from '../shared/player-wave/player-wave';
import PlayIcon from '../../assets/icons/player-play.svg';
import PauseIcon from '../../assets/icons/player-pause.svg';
import PrevIcon from '../../assets/icons/player-prev.svg';
import NextIcon from '../../assets/icons/player-next.svg';
import ShuffleIcon from '../../assets/icons/player-shuffle.svg';
import VolumeIcon from '../../assets/icons/player-volume.svg';
import VolumeMuteIcon from '../../assets/icons/player-volume-off.svg';
import RepeatIcon from '../../assets/icons/player-repeat.svg';
import RepeatOneIcon from '../../assets/icons/player-repeat-one.svg';
import MaxiIcon from '../../assets/icons/chevron-up.svg';
import { ThreeDotsSheetContext } from '../shared/three-dots-sheet/three-dots-sheet.context';
import VersionsDropdown from '../versions-dropdown/versions-dropdown';
import { sliderStyles } from './common';
import { useArtistLinks } from '../artist-link/artist-link';
import { ShareURL } from '../shared/share-url/share-url';
import { ThreeDotsButton } from '../shared/three-dots-button/three-dots-button';

export interface MiniPlayerProps {
    playerState: PlayerStateEnum;
}

export function MiniPlayer({ playerState }: MiniPlayerProps) {
    const {
        repeat,
        currentTrack,
        isShuffling,
        isRepeating,
        hasNextTrack,
        hasPrevTrack,
        nextTrack,
        prevTrack,
        toggleRepeat,
        toggleShuffle,
        volume,
        setVolume,
        setIsMaxiPlayer,
        replaceCurrentPlayingTrack,
        onPlay,
        onPause,
        isPreviewTrack,
    } = usePlayer();

    const { elapsed } = usePlayerDuration();

    const { openThreeDotsModalSheet } = useContext(ThreeDotsSheetContext);
    const { isDownload } = useHubSwitch();
    const { data: downloadAlbumData } = useDownloadAlbumById(currentTrack?.album_id, isDownload);
    const { isMobile } = useViewport();
    const volumeSliderRef = useRef<HTMLDivElement>(null);

    const { isAnonymous, setSelectedMedia } = useUserSettings();

    const setCurrentMediaInContext = useCallback(() => {
        if (isAnonymous && downloadAlbumData?.data) {
            const currentCenterMedia = downloadAlbumData?.data;
            if (currentCenterMedia) setSelectedMedia(currentCenterMedia as unknown as CreateCardCarouselItem, [downloadAlbumData?.data] as unknown as CreateCardCarouselItem[]);
        }
    }, [isAnonymous, downloadAlbumData?.data, setSelectedMedia]);

    useEffect(() => {
        if (isAnonymous) {
            setCurrentMediaInContext();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAnonymous]);

    const dragEvents = useDrag(
        ({ movement: [, mY], velocity: [, vY], down, event }) => {
            if (mY > 0 || down) return;
            if (volumeSliderRef.current?.contains(event.target as Node)) {
                return;
            }
            const swipeUpVelocity = vY > 0.5;
            if (swipeUpVelocity) {
                setIsMaxiPlayer(true);
            }
        },
        { delay: true }
    );

    const miniPlayerClickableProps = useMemo(() => {
        if (!isMobile) return {};

        return {
            role: 'button',
            tabIndex: 0,
            onClick: (e: MouseEvent) => {
                e.stopPropagation();
                setIsMaxiPlayer(true);
            },
            'aria-label': 'Open Maxi Player',
        };
    }, [isMobile, setIsMaxiPlayer]);

    const generateArtistLinks = useArtistLinks();

    useEffect(() => {
        if (currentTrack?.album_id) {
            Analytics.trackImpression('Album', `${currentTrack.album_id}`);
        }
    }, [currentTrack]);

    return (
        <div {...(dragEvents as any)()} {...miniPlayerClickableProps} className={styles['mini-player']}>
            <div
                className={styles['mini-player__container--supreme']}
                role="button"
                onKeyPress={() => { }}
                tabIndex={0}
                onClick={(e) => {
                    e.stopPropagation();
                    setIsMaxiPlayer(true);
                }}
            >
                <div className={styles['mini-player__details']}>
                    <img
                        src={appendQueryParams(currentTrack.cover_url, { key: 'dw', value: 64 })}
                        srcSet={`${appendQueryParams(currentTrack.cover_url, { key: 'dw', value: 128 })} 2x`}
                        alt={currentTrack.title}
                        className={styles['mini-player__details-cover']}
                    />
                    <div className={classNames(styles['mini-player__details-title-wrapper'], styles['mini-player__details-title-wrapper--has-right-fade-mobile'])}>
                        <div className={classNames(styles['mini-player__song-title-wrapper'])}>
                            <span className={classNames(styles['mini-player__details-title'])}>
                                {currentTrack.title.replace(`(${currentTrack.version})`, '')}
                            </span>
                            {currentTrack.versions && currentTrack.versions - 1 > 0 && (
                                <div className={styles['mini-player__version-dropdown']}>
                                    <VersionsDropdown<number>
                                        currentVersion={currentTrack.version}
                                        onClick={(version) => {
                                            replaceCurrentPlayingTrack(downloadAlbumWithMediaToQueueItem(downloadAlbumData.data, version));
                                        }}
                                        openThreeDotsMenu={(version, leftPos, topPos) => openThreeDotsModalSheet('mini-player-versions', version, leftPos + 220, topPos - 50, false, currentTrack?.album_id)}
                                        placeholder={convertToPluralIfNeeded(currentTrack.versions, 'Version')}
                                        options={downloadAlbumData?.data.media.map((media, index) => ({
                                            versionName: media.version.name,
                                            versionId: media.id,
                                            versionIndex: index,
                                            versionDuration: parseSeconds(media.estimated_duration || 0),
                                        }))}
                                        layoutType="mini"
                                        openDirection="upward"
                                    />
                                </div>
                            )}
                        </div>
                        <span className={styles['mini-player__details-artist']}>{generateArtistLinks(currentTrack.artist, currentTrack.artists)}</span>
                    </div>
                </div>
                <div className={styles['mini-player__controls']}>
                    <button
                        className={classNames(styles['mini-player__control'], styles['mini-player__control--prev'])}
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            prevTrack();
                        }}
                        disabled={!hasPrevTrack}
                        aria-label="Previous Track"
                    >
                        <PrevIcon />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            if (playerState === PlayerStateEnum.Playing) {
                                // Should this be controlled at context level?
                                // Should be the same for Maxi and mini
                                onPause();
                            } else {
                                onPlay();
                            }
                        }}
                        className={classNames(styles['mini-player__control'], {
                            [styles['mini-player__control--play']]: playerState !== PlayerStateEnum.Playing,
                            [styles['mini-player__control--pause']]: playerState === PlayerStateEnum.Playing,
                        })}
                        type="button"
                        aria-label={playerState === PlayerStateEnum.Playing ? 'Pause' : 'Play'}
                    >
                        {playerState === PlayerStateEnum.Playing ? <PauseIcon /> : <PlayIcon />}
                    </button>
                    <button
                        className={classNames(styles['mini-player__control'], styles['mini-player__control--next'])}
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            nextTrack();
                        }}
                        disabled={!hasNextTrack}
                        aria-label="Next Track"
                    >
                        <NextIcon />
                    </button>
                </div>
                <div className={styles['mini-player__right-section']}>
                    <div className={styles['mini-player__wave']}>
                        <div className={styles['mini-player__elapsed']}>{parseSeconds(elapsed)}</div>
                        {currentTrack && <PlayerWave />}
                        <div className={styles['mini-player__duration']}>{parseSeconds(isPreviewTrack ? currentTrack.estimated_duration : HLSPlayer.getDuration())}</div>
                    </div>
                    <div className={styles['mini-player__actions']}>
                        <button
                            className={classNames(styles['mini-player__action'], styles['mini-player__action--shuffle'], {
                                [styles['mini-player__action--active']]: isShuffling,
                            })}
                            type="button"
                            aria-label="Shuffle"
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleShuffle();
                            }}
                        >
                            <ShuffleIcon />
                        </button>
                        <button
                            className={classNames(styles['mini-player__action'], styles['mini-player__action--repeat'], {
                                [styles['mini-player__action--active']]: isRepeating,
                            })}
                            type="button"
                            aria-label="Repeat"
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleRepeat();
                            }}
                        >
                            {(repeat === 'off' || repeat === 'all') && <RepeatIcon />}
                            {repeat === 'one' && <RepeatOneIcon />}
                        </button>
                        <ShareURL actionTypeId={currentTrack?.album_id} actionType="mini-player" secondaryActionTypeId={currentTrack?.album_id} />
                        <div className={styles['mini-player__volume-action']}>
                            <button
                                className={styles['mini-player__volume-action--volume']}
                                type="button"
                                aria-label="Repeat"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setVolume(!volume ? 100 : 0);
                                }}
                            >
                                {volume ? <VolumeIcon /> : <VolumeMuteIcon />}
                            </button>
                            <div
                                className={styles['mini-player__volume-action--slider-container']}
                                tabIndex={0}
                                ref={volumeSliderRef}
                                role="button"
                                onClick={(e) => e.stopPropagation()}
                                onKeyPress={() => { }}
                            >
                                <Slider
                                    className={styles['mini-player__volume-action--slider']}
                                    min={0}
                                    max={100}
                                    step={1}
                                    value={volume}
                                    onChange={setVolume}
                                    vertical
                                    trackStyle={sliderStyles.supremeTrackStyle}
                                    handleStyle={sliderStyles.handleStyle}
                                />
                            </div>
                        </div>

                        <div className={styles['mini-player__menu-wrapper']}>
                            <ThreeDotsButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openThreeDotsModalSheet(
                                        'mini-player',
                                        currentTrack.id,
                                        e.currentTarget.getBoundingClientRect().left + 20,
                                        e.currentTarget.getBoundingClientRect().top + window.scrollY - (isDownload ? 140 : 180),
                                        false,
                                        currentTrack.album_id
                                    );
                                }}
                                hasTooltip />
                        </div>
                        <button
                            className={classNames(styles['mini-player__action'], styles['mini-player__action--maxi-player'])}
                            type="button"
                            aria-label="Open Maxi Player"
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsMaxiPlayer(true);
                            }}
                        >
                            <MaxiIcon />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
